import { Link } from 'react-router';

import { ROUTES } from '@/app/router/routes';
import { EmptyState } from '@/components/feedback/EmptyState';
import { AlertCircleIcon, ChevronRightIcon, SpinnerIcon, TimesheetIcon } from '@/components/icons';
import { PageHeading, SectionHeading } from '@/components/portal/PortalPage';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { TimesheetDeclaration } from './TimesheetDeclaration';
import { ErrorNotice, LoadingState } from '@/components/portal/PortalPage';
import { useMissions } from '@/features/portal/usePortal';

import { formatDays, formatMonth, STATE_HINTS, STATE_TONES } from './timesheet-labels';
import type { TimesheetStateCode } from './types';
import { useMyTimesheets } from './useTimesheets';

/**
 * Accueil du portail : les comptes rendus du consultant.
 *
 * ── Pourquoi l'ouverture d'un mois est ici ───────────────────────────────
 * Un CRA n'existe pas tant que personne ne l'a ouvert. Sans ce geste à portée
 * de main, un consultant arriverait sur une liste vide sans comprendre
 * comment commencer — c'est le cas au premier mois de chaque mission.
 */
export const TimesheetsPage = () => {
  const query = useMyTimesheets();
  const missions = useMissions();
  const items = query.data?.items ?? [];
  return (
    <div className="portal-page">
      <PageHeading
        title="Mes comptes rendus"
        description="Déclarez votre activité et suivez la validation de vos CRA."
        icon={<TimesheetIcon />}
      />

      {missions.isPending ? (
        <LoadingState>Chargement des missions…</LoadingState>
      ) : missions.isError ? (
        <ErrorNotice
          onRetry={() => {
            void missions.refetch();
          }}
        >
          {missions.error.message}
        </ErrorNotice>
      ) : missions.data.length > 0 ? (
        <TimesheetDeclaration missions={missions.data} />
      ) : null}

      {/* ── Mes comptes rendus ─────────────────────────────────────────── */}
      <section className="portal-surface">
        <SectionHeading
          title="Mes déclarations"
          description="Du mois le plus récent au plus ancien."
          action={
            query.data ? (
              <span className="portal-count">{query.data.meta.total} CRA</span>
            ) : undefined
          }
        />
        {query.isPending ? (
          <p role="status" className="text-ink-500 flex items-center gap-2 px-5 py-6 text-sm">
            <SpinnerIcon className="text-base" />
            Chargement de vos comptes rendus…
          </p>
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 px-5 py-6 text-sm text-red-800"
          >
            <AlertCircleIcon className="shrink-0 text-base" />
            {query.error.message}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                void query.refetch();
              }}
            >
              Réessayer
            </Button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            className="my-8"
            icon={<TimesheetIcon />}
            title="Aucun compte rendu"
            description={
              (missions.data?.length ?? 0) === 0
                ? 'Aucune mission pour le moment. Votre interlocuteur ubeefy vous préviendra au démarrage.'
                : 'Ouvrez un mois ci-dessus pour commencer à déclarer votre activité.'
            }
          />
        ) : (
          <ul className="portal-list">
            {items.map((timesheet) => (
              <li key={timesheet.id} className="border-navy-100 border-b last:border-b-0">
                <Link to={ROUTES.timesheet(timesheet.id)} className="portal-list-row">
                  <span className="portal-row-icon" aria-hidden>
                    <TimesheetIcon />
                  </span>
                  <span>
                    <strong className="text-navy-900 block text-sm font-semibold first-letter:uppercase">
                      {formatMonth(timesheet.periodMonth)}
                    </strong>
                    <span className="text-ink-500 mt-1 block text-xs wrap-anywhere">
                      {[timesheet.mission.clientName, timesheet.mission.title]
                        .filter(Boolean)
                        .join(' · ') || timesheet.mission.projectName}
                    </span>
                  </span>

                  <span className="portal-row-activity text-sm tabular-nums">
                    <span className="text-navy-900 font-medium">
                      {formatDays(timesheet.totals.workedDays)}
                    </span>
                    <span className="text-ink-400">
                      {' / '}
                      {timesheet.totals.expectedDays} j
                    </span>
                    <span className="portal-progress" aria-hidden>
                      <span
                        style={{
                          width: `${Math.min(100, timesheet.totals.expectedDays > 0 ? (Number(timesheet.totals.workedDays) / timesheet.totals.expectedDays) * 100 : 0)}%`,
                        }}
                      />
                    </span>
                  </span>

                  <span className="portal-row-state">
                    <Badge tone={STATE_TONES[timesheet.state.code] ?? 'neutral'}>
                      {timesheet.state.label}
                    </Badge>
                    <span className="text-ink-400 mt-0.5 block text-[11px]">
                      {STATE_HINTS[timesheet.state.code as TimesheetStateCode]}
                    </span>
                  </span>
                  <span className="portal-row-arrow" aria-hidden>
                    <ChevronRightIcon />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
